import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Schedule,
  Cancel,
  Warning,
  CheckCircle,
  Info,
  AccessTime,
  ArrowBack,
  Event,
  LocalHospital,
  CarCrash,
  Person,
  Email,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNavigation';
import useResponsive from '../hooks/useResponsive';

const RescheduleCancellationPolicy = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)',
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" fontWeight="600" color="white">
              Reschedule & Cancellation Policy
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Important Notice */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Important Notice
          </Typography>
          <Typography variant="body2">
            This policy applies to all bookings made through NIStylez. Please read carefully to understand our rescheduling and cancellation terms.
          </Typography>
        </Alert>

        {/* Rescheduling Policy */}
        <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Schedule sx={{ fontSize: 40, color: '#6366f1', mr: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#1a237e">
                Rescheduling Policy
              </Typography>
            </Box>
            
            <List sx={{ pl: 0 }}>
              <ListItem sx={{ px: 0, py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                        More than 12 hours before the appointment
                      </Typography>
                      <Chip 
                        label="Free rescheduling" 
                        color="success" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  }
                />
              </ListItem>

              <ListItem sx={{ px: 0, py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                        More than 6 hours before the appointment
                      </Typography>
                      <Chip 
                        label="10% fee from booking amount" 
                        color="warning" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  }
                />
              </ListItem>

              <ListItem sx={{ px: 0, py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Cancel sx={{ color: '#ef4444', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                        Less than 1 hour before the appointment
                      </Typography>
                      <Chip 
                        label="No rescheduling allowed - must cancel and rebook" 
                        color="error" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Cancellation Policy */}
        <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Cancel sx={{ fontSize: 40, color: '#ec4899', mr: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#1a237e">
                Cancellation Policy
              </Typography>
            </Box>
            
            <List sx={{ pl: 0 }}>
              <ListItem sx={{ px: 0, py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                        More than 24 hours before appointment
                      </Typography>
                      <Chip 
                        label="Free Cancellation" 
                        color="success" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  }
                />
              </ListItem>

              <ListItem sx={{ px: 0, py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Info sx={{ color: '#3b82f6', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                        More than 12 hours before the appointment
                      </Typography>
                      <Chip 
                        label="Full credit in wallet (valid for 90 days) - No refund" 
                        color="info" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  }
                />
              </ListItem>

              <ListItem sx={{ px: 0, py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                        Less than 3 hours before the appointment
                      </Typography>
                      <Chip 
                        label="50% credit (valid for 30 days) - No refund" 
                        color="warning" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  }
                />
              </ListItem>

              <ListItem sx={{ px: 0, py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Cancel sx={{ color: '#ef4444', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                        Less than 1 hour before the appointment or No-Show
                      </Typography>
                      <Chip 
                        label="No refund or credit" 
                        color="error" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* No-Show Policy */}
        <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden', border: '2px solid #ef4444' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Warning sx={{ fontSize: 40, color: '#ef4444', mr: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#1a237e">
                No-Show Policy
              </Typography>
            </Box>
            
            <List sx={{ pl: 0 }}>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Cancel sx={{ color: '#ef4444', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary="If the customer doesn't show up without canceling, no refund will be given."
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary="After two no-shows, the account gets flagged"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Emergency Exception Policy */}
        <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LocalHospital sx={{ fontSize: 40, color: '#6366f1', mr: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#1a237e">
                Emergency Exception Policy
              </Typography>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              In case of a verified emergency, customers can request a one-time waiver of cancellation or rescheduling fees.
            </Typography>

            <Typography variant="h6" fontWeight="600" color="#1a237e" sx={{ mb: 2 }}>
              Acceptable emergencies include:
            </Typography>
            
            <List sx={{ pl: 0, mb: 3 }}>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LocalHospital sx={{ color: '#ef4444', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText primary="Medical emergencies (doctor's note or hospital proof)" />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CarCrash sx={{ color: '#f59e0b', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText primary="Accidents (police report or insurance claim)" />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Person sx={{ color: '#6b7280', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText primary="Death in the family (proof may be requested)" />
              </ListItem>
            </List>

            <Typography variant="h6" fontWeight="600" color="#1a237e" sx={{ mb: 2 }}>
              How It Works:
            </Typography>
            
            <List sx={{ pl: 0 }}>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AccessTime sx={{ color: '#6366f1', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText primary="Customers must contact support within 48 hours of the missed appointment." />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Info sx={{ color: '#3b82f6', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText primary="Proof is required for approval." />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText primary="If approved, the customer receives full credit for a future booking." />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Need Help Section */}
        <Card sx={{ borderRadius: 3, overflow: 'hidden', background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Email sx={{ fontSize: 60, color: 'white', mb: 3 }} />
            <Typography variant="h4" fontWeight="bold" color="white" gutterBottom>
              Need Help?
            </Typography>
            <Typography variant="h6" color="white" sx={{ opacity: 0.9, mb: 3 }}>
              If you have any questions about our reschedule and cancellation policy, please contact us.
            </Typography>
            <Box sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: 2, 
              p: 3, 
              display: 'inline-block',
              backdropFilter: 'blur(10px)'
            }}>
              <Typography variant="h5" fontWeight="bold" color="white">
                nistylezaustralia@gmail.com
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default RescheduleCancellationPolicy;